import React, {useState, useEffect} from 'react'
import {Button, Modal, Form, Spinner, Row, Col, InputGroup, ButtonGroup} from 'react-bootstrap'
import {KTSVG} from '../../../../_metronic/helpers'
import {useHowToVideosAdmin} from '../../../hooks/useHowToVideos'
import {
  useCreateHowToVideoMutation,
  useUpdateHowToVideoMutation,
  useDeleteHowToVideoMutation,
  useToggleHowToVideoStatusMutation,
} from '../../../../services/cellcast'
import {HowToVideo} from '../../../../services/cellcast.type'
import {getCategoryDisplayName} from '../../../Utils/videoUtils'
import AdminVideoCard from './AdminVideoCard'

const AdminHowToVideosPage = () => {
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [videoToDelete, setVideoToDelete] = useState<string | null>(null)
  const [editingVideo, setEditingVideo] = useState<HowToVideo | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(undefined)
  const [categories, setCategories] = useState<{value: string; label: string}[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subTitle: '',
    subDescription: '',
    footerText: '',
    imagePath: '',
    videoUrl: '',
    category: '',
    feature: '',
    isActive: true,
  })

  const {
    videos,
    isLoading,
    searchTerm,
    setSearchTerm,
    totalVideos,
    currentPage,
    setCurrentPage,
    totalPages,
    refetch,
  } = useHowToVideosAdmin({
    pageSize: 10,
    category: categoryFilter,
    isActive: statusFilter,
  })

  // Extract unique categories from videos
  useEffect(() => {
    const uniqueCategories = Array.from(new Set(videos.map((v) => v.category).filter(Boolean)))
    setCategories(
      uniqueCategories.map((cat) => ({
        value: cat || '',
        label: cat ? getCategoryDisplayName(cat) : 'Uncategorized',
      }))
    )
  }, [videos])

  // Initialize mutation hooks
  const [createVideo, {isLoading: isCreating}] = useCreateHowToVideoMutation()
  const [updateVideo, {isLoading: isUpdating}] = useUpdateHowToVideoMutation()
  const [deleteVideo, {isLoading: isDeleting}] = useDeleteHowToVideoMutation()
  const [toggleStatus] = useToggleHowToVideoStatusMutation()

  // Initialize filters on component mount
  useEffect(() => {
    setCategoryFilter('')
    setStatusFilter(undefined)
  }, [])

  const handleShowModal = (video?: HowToVideo) => {
    if (video) {
      setEditingVideo(video)
      setFormData({
        title: video.title,
        description: video.description,
        subTitle: video.subTitle || '',
        subDescription: video.subDescription || '',
        footerText: video.footerText || '',
        imagePath: video.imagePath || '',
        videoUrl: video.videoUrl || '',
        category: video.category || '',
        feature: (video as any).feature || '',
        isActive: video.isActive ?? true,
      })
    } else {
      setEditingVideo(null)
      setFormData({
        title: '',
        description: '',
        subTitle: '',
        subDescription: '',
        footerText: '',
        imagePath: '',
        videoUrl: '',
        category: '',
        feature: '',
        isActive: true,
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingVideo(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingVideo) {
        await updateVideo({id: editingVideo._id, ...formData}).unwrap()
      } else {
        await createVideo(formData).unwrap()
      }
      handleCloseModal()
      refetch()
    } catch (error) {
      console.error('Error saving video:', error)
    }
  }

  const handleDeleteClick = (id: string) => {
    setVideoToDelete(id)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!videoToDelete) return

    try {
      await deleteVideo(videoToDelete).unwrap()
      refetch()
    } catch (error) {
      console.error('Error deleting video:', error)
    } finally {
      setShowDeleteModal(false)
      setVideoToDelete(null)
    }
  }

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await toggleStatus({id, isActive: !currentStatus}).unwrap()
      refetch()
    } catch (error) {
      console.error('Error toggling video status:', error)
    }
  }

  const handleClearFilters = () => {
    setCategoryFilter('')
    setStatusFilter(undefined)
    setSearchTerm('')
    // No need to manually call setCurrentPage(1) or refetch() as the useEffect in the hook will handle this
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'></div>

        <div className='card-toolbar'>
          <div className='d-flex align-items-center gap-2'>
            <Button
              variant='light-primary'
              className='btn-icon me-2'
              onClick={() => setShowFilters(!showFilters)}
              title='Toggle filters'
            >
              <i className={`bi bi-${showFilters ? 'funnel-fill' : 'funnel'} fs-2`}></i>
            </Button>

            <ButtonGroup className='me-4'>
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'light'}
                onClick={() => setViewMode('grid')}
                title='Grid View'
              >
                <i className='bi bi-grid-3x3-gap-fill fs-2'></i>
              </Button>
              <Button
                variant={viewMode === 'table' ? 'primary' : 'light'}
                onClick={() => setViewMode('table')}
                title='Table View'
              >
                <i className='bi bi-list-ul fs-2'></i>
              </Button>
            </ButtonGroup>

            <Button variant='primary' onClick={() => handleShowModal()}>
              <i className='bi bi-plus-lg me-2'></i>
              Add Video
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className='card-body border-bottom'>
          <Row className='g-3'>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <Form.Control
                    type='text'
                    placeholder='Search videos...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button variant='light' onClick={() => setSearchTerm('')} title='Clear search'>
                      <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value || '')}
                >
                  <option value=''>All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={statusFilter === undefined ? '' : String(statusFilter)}
                  onChange={(e) =>
                    setStatusFilter(e.target.value === '' ? undefined : e.target.value === 'true')
                  }
                >
                  <option value=''>All Status</option>
                  <option value='true'>Active</option>
                  <option value='false'>Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className='d-flex align-items-end'>
              <Button
                variant='light-danger'
                className='w-100'
                onClick={handleClearFilters}
                disabled={!searchTerm && !categoryFilter && statusFilter === undefined}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </div>
      )}

      <div className='card-body py-4'>
        {isLoading ? (
          <div className='d-flex justify-content-center py-10'>
            <Spinner animation='border' />
          </div>
        ) : viewMode === 'grid' ? (
          <div className='row g-6'>
            {videos.map((video) => (
              <div key={video._id} className='col-xl-3 col-lg-4 col-md-6 mb-6'>
                <AdminVideoCard
                  video={video}
                  onEdit={handleShowModal}
                  onDelete={handleDeleteClick}
                  onToggleStatus={handleToggleStatus}
                  isDeleting={isDeleting}
                />
              </div>
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className='text-center py-10'>
            <div className='text-muted'>No videos found</div>
          </div>
        ) : viewMode === 'table' ? (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='min-w-150px'>Title</th>
                  <th className='min-w-140px'>Category</th>
                  <th className='min-w-120px'>Status</th>
                  <th className='min-w-100px text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video) => (
                  <tr key={video._id}>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div className='symbol symbol-45px me-5'>
                          <img
                            src={
                              video.imagePath ||
                              'https://cel-public-assets.s3.ap-southeast-2.amazonaws.com/cel_coming_soon.png'
                            }
                            alt={video.title}
                            className='h-45px w-45px'
                          />
                        </div>
                        <div className='d-flex justify-content-start flex-column'>
                          <span className='text-dark fw-bold text-hover-primary fs-6'>
                            {video.title}
                          </span>
                          <span className='text-muted fw-semibold text-muted d-block fs-7'>
                            {video.description}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className='badge badge-light-primary'>
                        {getCategoryDisplayName(video.category)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge badge-light-${video.isActive ? 'success' : 'danger'}`}
                      >
                        {video.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className='d-flex justify-content-end flex-shrink-0'>
                        <Button
                          variant='light'
                          size='sm'
                          className='me-2'
                          onClick={() => handleShowModal(video)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant='light-danger'
                          size='sm'
                          onClick={() => handleDeleteClick(video._id)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className='row g-6'>
            {videos.map((video) => (
              <div key={video._id} className='col-xl-3 col-lg-4 col-md-6 mb-6'>
                <AdminVideoCard
                  video={video}
                  onEdit={handleShowModal}
                  onDelete={handleDeleteClick}
                  onToggleStatus={handleToggleStatus}
                  isDeleting={isDeleting}
                />
              </div>
            ))}
          </div>
        )}

        {/* Results info and Pagination */}
        <div className='d-flex flex-stack flex-wrap pt-10'>
          <div className='fs-6 fw-semibold text-gray-700'>
            Showing {videos.length} of {totalVideos} videos
          </div>

          {totalPages > 1 && (
            <ul className='pagination mb-0'>
              <li className={`page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  type='button'
                  className='page-link'
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  title='Previous page'
                >
                  <i className='previous'></i>
                </button>
              </li>

              {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    <button
                      type='button'
                      className='page-link'
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li className={`page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  type='button'
                  className='page-link'
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title='Next page'
                >
                  <i className='next'></i>
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>{editingVideo ? 'Edit Video' : 'Add New Video'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className='row'>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label>Title *</Form.Label>
                  <Form.Control
                    type='text'
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label>Sub Title</Form.Label>
                  <Form.Control
                    type='text'
                    value={formData.subTitle}
                    onChange={(e) => setFormData({...formData, subTitle: e.target.value})}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className='mb-3'>
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as='textarea'
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Sub Description</Form.Label>
              <Form.Control
                as='textarea'
                rows={3}
                value={formData.subDescription}
                onChange={(e) => setFormData({...formData, subDescription: e.target.value})}
              />
            </Form.Group>

            <div className='row'>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label>Video URL</Form.Label>
                  <Form.Control
                    type='url'
                    value={formData.videoUrl}
                    onChange={(e) => setFormData({...formData, videoUrl: e.target.value})}
                    placeholder='https://www.loom.com/embed/...'
                  />
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group className='mb-3'>
                  <Form.Label>Image URL</Form.Label>
                  <Form.Control
                    type='url'
                    value={formData.imagePath}
                    onChange={(e) => setFormData({...formData, imagePath: e.target.value})}
                    placeholder='https://cdn.loom.com/sessions/thumbnails/...'
                  />
                </Form.Group>
              </div>
            </div>

            <div className='row'>
              <div className='col-md-4'>
                <Form.Group className='mb-3'>
                  <Form.Label>Category</Form.Label>
                  <Form.Control
                    as='select'
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    <option value=''>Select Category</option>
                    <option value='getting-started'>Getting Started</option>
                    <option value='messaging'>Messaging</option>
                    <option value='automation'>Automation</option>
                    <option value='contacts'>Contacts</option>
                    <option value='reports'>Reports</option>
                    <option value='integrations'>Integrations</option>
                    <option value='advanced'>Advanced</option>
                  </Form.Control>
                </Form.Group>
              </div>
              <div className='col-md-4'>
                <Form.Group className='mb-3'>
                  <Form.Label>Feature</Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='Enter feature name'
                    value={formData.feature}
                    onChange={(e) => setFormData({...formData, feature: e.target.value})}
                  />
                </Form.Group>
              </div>
              <div className='col-md-4 d-flex align-items-center'>
                <Form.Group className='mb-3'>
                  <Form.Label>&nbsp;</Form.Label>
                  <Form.Check
                    type='checkbox'
                    label='Active'
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className='mb-3'>
              <Form.Label>Footer Text</Form.Label>
              <Form.Control
                type='text'
                value={formData.footerText}
                onChange={(e) => setFormData({...formData, footerText: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type='submit' variant='primary' disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? 'Saving...' : editingVideo ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this video? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant='danger' onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <span className='spinner-border spinner-border-sm align-middle me-1' />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default AdminHowToVideosPage
